/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React from "react";
import { Box, Flex, Text, Select } from "@chakra-ui/react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { useTheme } from "../hooks/useTheme";
import { formatNumber } from "../../utils/formatNumber";

/**
 * Unified Chart Component
 * Provides consistent chart styling across all admin dashboard pages
 * Supports: Bar, Line, Pie, Area charts
 */
export default function UnifiedChart({
  type = "bar", // 'bar', 'line', 'pie', 'area'
  data = [],
  dataKey = "value",
  nameKey = "name",
  height = 400,
  title,
  showLegend = false,
  showGrid = true,
  color = null, // Override default color
  gradient = true,
  radius = [10, 10, 0, 0], // For bar charts
  ...props
}) {
  const { colors, isArabic, isDark, dir, textAlign } = useTheme();
  
  const chartColor = color || colors.mainColor;
  const chartColorHover = colors.mainColorHover;

  // Chart configuration with enhanced styling
  const chartConfig = {
    bar: {
      component: BarChart,
      children: (
        <Bar
          dataKey={dataKey}
          fill={gradient ? `url(#colorGradient)` : chartColor}
          radius={[8, 8, 0, 0]}
          barSize={60}
          animationDuration={1200}
          animationBegin={0}
          isAnimationActive={true}
          style={{
            filter: isDark ? 'drop-shadow(0 4px 8px rgba(255, 215, 0, 0.3))' : 'drop-shadow(0 2px 4px rgba(106, 17, 203, 0.2))',
            transition: 'all 0.3s ease',
          }}
        />
      ),
    },
    line: {
      component: LineChart,
      children: (
        <>
          <Line
            type="monotone"
            dataKey={dataKey}
            stroke={chartColor}
            strokeWidth={3}
            dot={{ fill: chartColor, r: 5 }}
            activeDot={{ r: 8 }}
            animationDuration={1200}
          />
          {gradient && (
            <Area
              type="monotone"
              dataKey={dataKey}
              stroke={chartColor}
              fill={`url(#colorGradient)`}
              fillOpacity={0.1}
            />
          )}
        </>
      ),
    },
    area: {
      component: AreaChart,
      children: (
        <Area
          type="monotone"
          dataKey={dataKey}
          stroke={chartColor}
          fill={gradient ? `url(#colorGradient)` : chartColor}
          fillOpacity={0.6}
          animationDuration={1200}
        />
      ),
    },
    pie: {
      component: PieChart,
      children: (
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          labelLine={false}
          label={({ name, percent }) =>
            `${name}: ${Math.round(percent * 100)}%`
          }
                    outerRadius={120}
          fill={chartColor}
          dataKey={dataKey}
          animationDuration={1200}
        >
          {data.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={
                index % 4 === 0
                  ? chartColor
                  : index % 4 === 1
                  ? colors.mainColorLight
                  : index % 4 === 2
                  ? chartColorHover
                  : colors.badgeInfo
              }
            />
          ))}
        </Pie>
      ),
    },
  };

  const ChartComponent = chartConfig[type]?.component || BarChart;
  const chartChildren = chartConfig[type]?.children || chartConfig.bar.children;

  if (!data || data.length === 0) {
    return (
      <Box
        bg={colors.cardBg}
        borderRadius="xl"
        borderWidth="1px"
        borderColor={colors.borderColor}
        p={8}
        textAlign="center"
      >
        <Text color={colors.subText}>
          {isArabic ? "لا توجد بيانات لعرضها" : "No data to display"}
        </Text>
      </Box>
    );
  }

  return (
    <Box
      bg={colors.cardBg}
      borderRadius="xl"
      borderWidth="1px"
      borderColor={colors.borderColor}
      p={6}
      boxShadow={isDark 
        ? "0 4px 6px rgba(0, 0, 0, 0.3), inset 0 0 0 1px rgba(255, 215, 0, 0.05)" 
        : "0 2px 4px rgba(0, 0, 0, 0.05), inset 0 0 0 1px rgba(106, 17, 203, 0.05)"}
      transition="all 0.3s ease"
      _hover={{
        boxShadow: isDark 
          ? "0 8px 12px rgba(0, 0, 0, 0.4), inset 0 0 0 1px rgba(255, 215, 0, 0.1)" 
          : "0 4px 8px rgba(0, 0, 0, 0.1), inset 0 0 0 1px rgba(106, 17, 203, 0.1)",
      }}
      {...props}
    >
      {title && (
        <Text
          fontSize="xl"
          fontWeight="bold"
          color={colors.mainColor}
          mb={4}
          textAlign={textAlign}
        >
          {title}
        </Text>
      )}

      <ResponsiveContainer width="100%" height={height}>
        <ChartComponent
          data={data}
          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
          {...(type === "pie" ? {} : { barGap: 25 })}
        >
          {gradient && type !== "pie" && (
            <defs>
              <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={chartColor} stopOpacity={isDark ? 1 : 0.95} />
                <stop offset="50%" stopColor={chartColor} stopOpacity={isDark ? 0.8 : 0.7} />
                <stop offset="100%" stopColor={chartColor} stopOpacity={isDark ? 0.5 : 0.4} />
              </linearGradient>
              <filter id="glow">
                <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                <feMerge>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>
          )}

          {showGrid && type !== "pie" && (
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={isDark ? "#475569" : "#e2e8f0"}
              opacity={isDark ? 0.4 : 0.3}
              vertical={false}
              strokeWidth={1}
            />
          )}

          {type !== "pie" && (
            <>
              <XAxis
                dataKey={nameKey}
                stroke={isDark ? "#94a3b8" : "#64748b"}
                fontSize={13}
                tickLine={false}
                axisLine={false}
                style={{ direction: dir }}
                tick={{ fill: isDark ? "#cbd5e1" : "#475569" }}
                tickFormatter={(value) =>
                  typeof value === "number" ? formatNumber(value) : value
                }
                
              />
              <YAxis
                stroke={isDark ? "#94a3b8" : "#64748b"}
                fontSize={13}
                tickLine={false}
                axisLine={false}
                tickMargin={10}
                tick={{ fill: isDark ? "#cbd5e1" : "#475569" }}
                tickFormatter={(value) => {
                  if (typeof value === "number") {
                    if (value >= 1_000_000) return `${Math.round(value / 1_000_000)}M`;
                    if (value >= 1_000) return `${Math.round(value / 1_000)}K`;
                    return formatNumber(value);
                  }
                  return value;
                }}
                
              />
            </>
          )}

          <Tooltip
            contentStyle={{
              backgroundColor: isDark ? "#1e293b" : "white",
              borderRadius: "12px",
              border: `1px solid ${isDark ? "#475569" : "#e2e8f0"}`,
              color: colors.textColor,
              boxShadow: isDark 
                ? "0 8px 16px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 215, 0, 0.1)" 
                : "0 4px 12px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(106, 17, 203, 0.1)",
              padding: "12px 16px",
              fontSize: "14px",
              fontWeight: "500",
            }}
            cursor={type === "bar" ? { fill: chartColor, opacity: 0.1 } : { stroke: chartColor, strokeWidth: 2, strokeDasharray: "5 5" }}
            formatter={(value, name) => {
              if (typeof value === "number") {
                return [
                     `${formatNumber(value)} ${name === "amount" ? "IQD" : ""}`,
                  isArabic ? "القيمة" : "Value"
                ];
              }
              return [value, name || ""];
            }}
            labelStyle={{
              color: isDark ? "#cbd5e1" : "#475569",
              fontWeight: "600",
              marginBottom: "4px",
            }}
          />

          {showLegend && <Legend wrapperStyle={{ color: colors.textColor }} />}

          {chartChildren}
        </ChartComponent>
      </ResponsiveContainer>
    </Box>
  );
}

